import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ACSTheme, ACSSpacing, ThemeConfig } from '../utils/interfaces';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'acs-theme-config';
  private currentThemeConfig: ThemeConfig = {
    theme: 'default',
    spacing: 'default'
  };

  private themeSubject = new BehaviorSubject<ThemeConfig>(this.currentThemeConfig);
  public theme$ = this.themeSubject.asObservable();

  constructor() {
    this.loadThemeFromStorage();
    this.applyTheme(this.currentThemeConfig);
  }

  /**
   * Set the current theme
   */
  setTheme(theme: ACSTheme): void {
    this.currentThemeConfig = {
      ...this.currentThemeConfig,
      theme
    };
    this.applyTheme(this.currentThemeConfig);
    this.saveThemeToStorage();
    this.themeSubject.next(this.currentThemeConfig);
  }

  /**
   * Set the spacing preference
   */
  setSpacing(spacing: ACSSpacing): void {
    this.currentThemeConfig = {
      ...this.currentThemeConfig,
      spacing
    };
    this.applyTheme(this.currentThemeConfig);
    this.saveThemeToStorage();
    this.themeSubject.next(this.currentThemeConfig);
  }

  /**
   * Set custom colors (for chapter-specific theming)
   */
  setCustomColors(colors: { primary?: string; secondary?: string; accent?: string }): void {
    this.currentThemeConfig = {
      ...this.currentThemeConfig,
      customColors: colors
    };
    this.applyCustomColors(colors);
    this.saveThemeToStorage();
    this.themeSubject.next(this.currentThemeConfig);
  }

  /**
   * Get current theme configuration
   */
  getCurrentTheme(): ThemeConfig {
    return { ...this.currentThemeConfig };
  }

  /**
   * Reset to default theme
   */
  resetTheme(): void {
    this.currentThemeConfig = {
      theme: 'default',
      spacing: 'default'
    };
    this.applyTheme(this.currentThemeConfig);
    this.removeCustomColors();
    this.saveThemeToStorage();
    this.themeSubject.next(this.currentThemeConfig);
  }

  /**
   * Toggle between light and dark themes
   */
  toggleDarkMode(): void {
    const newTheme = this.currentThemeConfig.theme === 'dark' ? 'default' : 'dark';
    this.setTheme(newTheme);
  }

  /**
   * Apply theme to document
   */
  private applyTheme(config: ThemeConfig): void {
    const body = document.body;
    
    // Remove existing theme classes
    body.removeAttribute('data-theme');
    body.removeAttribute('data-spacing');
    
    // Apply new theme
    if (config.theme !== 'default') {
      body.setAttribute('data-theme', config.theme);
    }
    
    if (config.spacing !== 'default') {
      body.setAttribute('data-spacing', config.spacing);
    }
  }

  /**
   * Apply custom colors to CSS custom properties
   */
  private applyCustomColors(colors: { primary?: string; secondary?: string; accent?: string }): void {
    const root = document.documentElement;
    
    if (colors.primary) {
      root.style.setProperty('--acs-primary-blue', colors.primary);
      root.style.setProperty('--acs-primary', colors.primary);
    }
    
    if (colors.secondary) {
      root.style.setProperty('--acs-steel-blue', colors.secondary);
      root.style.setProperty('--acs-secondary', colors.secondary);
    }
    
    if (colors.accent) {
      root.style.setProperty('--acs-selection', colors.accent);
      root.style.setProperty('--acs-info', colors.accent);
    }
  }

  /**
   * Remove custom colors
   */
  private removeCustomColors(): void {
    const root = document.documentElement;
    root.style.removeProperty('--acs-primary-blue');
    root.style.removeProperty('--acs-primary');
    root.style.removeProperty('--acs-steel-blue');
    root.style.removeProperty('--acs-secondary');
    root.style.removeProperty('--acs-selection');
    root.style.removeProperty('--acs-info');
  }

  /**
   * Save theme configuration to localStorage
   */
  private saveThemeToStorage(): void {
    try {
      localStorage.setItem(this.THEME_KEY, JSON.stringify(this.currentThemeConfig));
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }

  /**
   * Load theme configuration from localStorage
   */
  private loadThemeFromStorage(): void {
    try {
      const saved = localStorage.getItem(this.THEME_KEY);
      if (saved) {
        const config = JSON.parse(saved) as ThemeConfig;
        this.currentThemeConfig = {
          theme: config.theme || 'default',
          spacing: config.spacing || 'default',
          customColors: config.customColors
        };
        
        if (config.customColors) {
          this.applyCustomColors(config.customColors);
        }
      }
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
    }
  }

  /**
   * Get available themes
   */
  getAvailableThemes(): Array<{ value: ACSTheme; label: string; description: string }> {
    return [
      { 
        value: 'default', 
        label: 'ACS Default', 
        description: 'Standard ACS brand colors and styling' 
      },
      { 
        value: 'high-contrast', 
        label: 'High Contrast', 
        description: 'Enhanced contrast for accessibility' 
      },
      { 
        value: 'dark', 
        label: 'Dark Theme', 
        description: 'Dark color scheme for low-light environments' 
      }
    ];
  }

  /**
   * Get available spacing options
   */
  getAvailableSpacing(): Array<{ value: ACSSpacing; label: string; description: string }> {
    return [
      { 
        value: 'compact', 
        label: 'Compact', 
        description: 'Reduced spacing for dense layouts' 
      },
      { 
        value: 'default', 
        label: 'Default', 
        description: 'Standard ACS spacing' 
      },
      { 
        value: 'comfortable', 
        label: 'Comfortable', 
        description: 'Increased spacing for better readability' 
      }
    ];
  }
}